import React, { useState } from 'react';
import { Box, Button, Text, Table, Input, Select, Checkbox, Grid, GridItem, Switch } from '@bigcommerce/big-design';
import { DeleteIcon, AddIcon, StarIcon, CheckIcon } from '@bigcommerce/big-design-icons';

/**
 * Component quản lý các tiêu chí đánh giá
 * Default criteria được load từ server (server/lib/utils.js -> getDefaultReviewCriteria)
 * @param {Array} criteria - Danh sách tiêu chí hiện tại (từ server defaultSettings)
 * @param {Function} onChange - Callback khi có thay đổi
 */
const ReviewCriteriaManager = ({ criteria, onChange }) => {
    const [editingIndex, setEditingIndex] = useState(-1);
    const [editingLabel, setEditingLabel] = useState(''); // Local state để lưu giá trị đang edit
    const [newCriterion, setNewCriterion] = useState({ label: '', type: 'rating', enabled: true, required: false });

    // Deep copy criteria để tránh read-only issues
    // Default criteria được load từ server (getDefaultReviewCriteria trong server/lib/utils.js)
    const currentCriteria = criteria.map(criterion => ({ ...criterion }));

    const handleToggleEnabled = (index) => {
        const updatedCriteria = currentCriteria.map((criterion, i) =>
            i === index
                ? { ...criterion, enabled: !criterion.enabled }
                : { ...criterion }
        );
        onChange(updatedCriteria);
    };

    const handleToggleRequired = (index) => {
        const updatedCriteria = currentCriteria.map((criterion, i) =>
            i === index
                ? { ...criterion, required: !criterion.required }
                : { ...criterion }
        );
        onChange(updatedCriteria);
    };

    const handleStartEdit = (index) => {
        setEditingIndex(index);
        setEditingLabel(currentCriteria[index].label);
    };

    const handleSaveLabel = () => {
        if (editingIndex === -1) return;

        const updatedCriteria = currentCriteria.map((criterion, i) =>
            i === editingIndex
                ? { ...criterion, label: editingLabel }
                : { ...criterion }
        );
        onChange(updatedCriteria);
        setEditingIndex(-1);
        setEditingLabel('');
    };

    const handleCancelEdit = () => {
        setEditingIndex(-1);
        setEditingLabel('');
    };

    const handleAddCriterion = () => {
        if (!newCriterion.label.trim()) return;

        const updatedCriteria = [...currentCriteria.map(criterion => ({ ...criterion })), {
            ...newCriterion,
            id: `custom_${Date.now()}`,
        }];
        onChange(updatedCriteria);
        setNewCriterion({ label: '', type: 'rating', enabled: true, required: false });
    };

    const handleDeleteCriterion = (index) => {
        const updatedCriteria = currentCriteria
            .filter((_, i) => i !== index)
            .map(criterion => ({ ...criterion }));
        onChange(updatedCriteria);
    };

    return (
        <Box>
            <Table
                columns={[
                    {
                        header: 'Criteria',
                        hash: 'label',
                        render: (item) => editingIndex === item.index ? (
                            <Input
                                value={editingLabel}
                                onChange={(e) => setEditingLabel(e.target.value)}
                                onBlur={handleSaveLabel}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveLabel();
                                    if (e.key === 'Escape') handleCancelEdit();
                                }}
                                autoFocus
                            />
                        ) : (
                            <Box style={{cursor: 'pointer'}} onClick={() => handleStartEdit(item.index)}>
                                {item.label}
                            </Box>
                        )
                    },
                    {
                        header: 'Type',
                        hash: 'type',
                        render: item => item.type === 'rating'
                            ? <><StarIcon color="warning" size="medium" /> Rating</>
                            : <><CheckIcon color="success" size="medium" /> Yes/No</>
                    },
                    {
                        header: 'Enabled',
                        hash: 'enabled',
                        render: (item) => (
                            <Switch
                                checked={item.enabled}
                                onChange={() => handleToggleEnabled(item.index)}
                            />
                        )
                    },
                    {
                        header: 'Required',
                        hash: 'required',
                        render: (item) => (
                            <Switch
                                checked={item.required}
                                onChange={() => handleToggleRequired(item.index)}
                                disabled={!item.enabled}
                            />
                        )
                    },
                    {
                        header: 'Actions',
                        hash: 'actions',
                        render: (item) => (
                            <Button
                                variant="subtle"
                                actionType="destructive"
                                onClick={() => handleDeleteCriterion(item.index)}
                            >
                                <DeleteIcon />
                            </Button>
                        )
                    },
                ]}
                items={currentCriteria.map((item, index) => ({ ...item, index }))}
                keyField='id'
            />

            {/* Thêm tiêu chí mới */}
            <Box marginTop="medium" padding="medium" backgroundColor="secondary10" borderRadius="normal">
                <Text bold marginBottom="small"><AddIcon size="medium" /> Add New Criteria:</Text>
                <Grid gridColumns="1fr auto auto auto">
                    <GridItem>
                        <Input
                            placeholder="Enter criteria name (e.g: Color, Size...)"
                            value={newCriterion.label}
                            onChange={(e) => setNewCriterion({...newCriterion, label: e.target.value})}
                        />
                    </GridItem>
                    <GridItem>
                        <Select
                            value={newCriterion.type}
                            onOptionChange={(value) => setNewCriterion({...newCriterion, type: value})}
                            options={[
                                { value: 'rating', content: 'Rating (1-5 stars)' },
                                { value: 'boolean', content: 'Yes/No' },
                            ]}
                            filterable={false}
                        />
                    </GridItem>
                    <GridItem>
                        <Checkbox
                            label="Required"
                            checked={newCriterion.required}
                            onChange={(e) => setNewCriterion({...newCriterion, required: e.target.checked})}
                        />
                    </GridItem>
                    <GridItem>
                        <Button onClick={handleAddCriterion} disabled={!newCriterion.label.trim()}>
                            Add
                        </Button>
                    </GridItem>
                </Grid>
            </Box>
        </Box>
    );
};

export default ReviewCriteriaManager;
